import React from "react";
import { Badge, Button, Card, Col, Form, Overlay, Popover, Row, Spinner } from "react-bootstrap";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import Table from "../../../layout/table";
import Hamburger from "../../../layout/kebab";
import ConfirmModal from "../../../layout/modal";
import { withNavigation } from "../../../utils/wrapper";
import { format as formatDate } from "../../../utils/date";
import { isGuid } from "../../../utils/string";
import { send as sendAlert } from "../../../actions/alert";
import {
    deleteEnvelopeGlobally,
    duplicateEnvelope,
    listEnvelopes,
    remindEnvelope
} from "../../../actions/signatures";

const STATUS_VARIANTS = {
    draft: "secondary",
    sent: "primary",
    viewed: "info",
    partially_signed: "warning",
    completed: "success",
    declined: "danger",
    voided: "dark",
    expired: "dark",
    error: "danger"
};

const statusLabel = (status) => String(status || "draft").replace(/_/g, " ").toUpperCase();

const envelopeTitle = (row = {}) => (
    row.subject ||
    row.displayName ||
    row.Documents?.[0]?.displayName ||
    row.Documents?.[0]?.originalFileName ||
    "Signature request"
);

const signerSummary = (row = {}) => {
    const signers = (row.Recipients || [])
        .filter((recipient) => recipient.recipientType === "signer")
        .map((recipient) => `${recipient.name}${recipient.status ? ` (${statusLabel(recipient.status)})` : ""}`);

    return signers.length ? signers.join(", ") : "No signers";
};

const canRemind = (row = {}) => ["sent", "viewed", "partially_signed"].includes(row.status);

const responseError = (response, fallback) => (
    response?.error?.message ||
    response?.error ||
    fallback
);

class SignatureRequests extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            page: 0,
            size: 10,
            search: "",
            order: "updatedAt",
            index: 3,
            direction: "DESC",
            items: [],
            pages: 0,
            loading: false,
            error: "",
            actionLoading: "",
            menu: {
                show: false,
                uuid: null,
                row: null,
                target: null
            },
            deleteModal: {
                show: false,
                row: null,
                confirmation: ""
            }
        };
    }

    componentDidMount = async () => {
        await this.loadData();
    }

    componentDidUpdate = async (prevProps) => {
        if (prevProps.person !== this.props.person) {
            await this.loadData();
        }
    }

    loadData = async () => {
        if (!isGuid(this.props.person)) {
            this.setState({
                items: [],
                pages: 0,
                loading: false,
                error: "",
                menu: {
                    show: false,
                    uuid: null,
                    row: null,
                    target: null
                }
            });
            return;
        }

        this.setState({ loading: true, error: "" });
        const response = await this.props.listEnvelopes({
            personUuid: this.props.person,
            limit: 250
        });

        if (!response?.success) {
            this.setState({
                items: [],
                pages: 0,
                loading: false,
                error: response?.error?.message || response?.error || "Signature requests could not be loaded."
            });
            return;
        }

        this.setState({
            items: response.items || [],
            loading: false
        }, this.refreshPages);
    }

    refreshPages = () => {
        const count = this.filteredItems.length;
        this.setState({
            pages: count > 0 ? Math.ceil(count / Number(this.state.size)) : 0
        });
    }

    get filteredItems() {
        const search = String(this.state.search || "").trim().toLowerCase();
        const rows = [...(this.state.items || [])];

        const filtered = search
            ? rows.filter((row) => [
                envelopeTitle(row),
                row.status,
                signerSummary(row)
            ].some((value) => String(value || "").toLowerCase().includes(search)))
            : rows;

        const order = this.state.order;
        const factor = this.state.direction === "DESC" ? -1 : 1;

        return filtered.sort((a, b) => {
            const av = order === "title" ? envelopeTitle(a) : a[order];
            const bv = order === "title" ? envelopeTitle(b) : b[order];
            return String(av || "").localeCompare(String(bv || "")) * factor;
        });
    }

    get pagedItems() {
        const start = this.state.page * this.state.size;
        return this.filteredItems.slice(start, start + this.state.size);
    }

    onRequestFormat = (_value, row) => (
        <Button variant="link" className="p-0 align-baseline" onClick={() => this.props.navigate(`/dashboard/signatures/${row.uuid}`)}>
            {envelopeTitle(row)}
        </Button>
    );

    onStatusFormat = (value) => (
        <Badge bg={STATUS_VARIANTS[value] || "secondary"}>{statusLabel(value)}</Badge>
    );

    onSignersFormat = (_value, row) => signerSummary(row);

    onDateFormat = (value) => formatDate(value, true);

    openHamburger = (event, row) => {
        event.preventDefault();
        this.setState({
            menu: {
                show: true,
                uuid: row.uuid,
                row,
                target: event.currentTarget
            }
        });
    }

    closeHamburger = () => {
        this.setState((prev) => ({
            menu: {
                ...prev.menu,
                show: false,
                target: null
            }
        }));
    }

    onActionFormat = (_value, row) => (
        <Hamburger variant="link" vertical={false} onClick={(event) => this.openHamburger(event, row)} />
    );

    duplicateRequest = async () => {
        const row = this.state.menu.row;
        if (!row?.uuid) return;

        this.closeHamburger();
        this.setState({ actionLoading: `duplicate:${row.uuid}` });
        const response = await this.props.duplicateEnvelope({ uuid: row.uuid });
        this.setState({ actionLoading: "" });

        if (!response?.success) {
            this.props.sendAlert(responseError(response, "Signature request could not be duplicated."), "error");
            return;
        }

        const uuid = response.item?.uuid || response.uuid;
        this.props.sendAlert("Signature request duplicated.", "success");
        if (uuid) {
            this.props.navigate(`/dashboard/signatures/${uuid}`);
        } else {
            await this.loadData();
        }
    }

    remindRequest = async () => {
        const row = this.state.menu.row;
        if (!row?.uuid) return;

        if (!canRemind(row)) {
            this.closeHamburger();
            this.props.sendAlert("Only active signature requests can be reminded.", "warning");
            return;
        }

        this.closeHamburger();
        this.setState({ actionLoading: `remind:${row.uuid}` });
        const response = await this.props.remindEnvelope({ uuid: row.uuid });
        this.setState({ actionLoading: "" });

        if (!response?.success) {
            this.props.sendAlert(responseError(response, "Signature reminder could not be sent."), "error");
            return;
        }

        this.props.sendAlert("Reminder sent to pending signers.", "success");
        await this.loadData();
    }

    openDeleteModal = () => {
        const row = this.state.menu.row;
        if (!row?.uuid) return;

        this.closeHamburger();
        this.setState({
            deleteModal: {
                show: true,
                row,
                confirmation: ""
            }
        });
    }

    closeDeleteModal = () => {
        this.setState({
            deleteModal: {
                show: false,
                row: null,
                confirmation: ""
            }
        });
    }

    onDeleteConfirmationChange = (event) => {
        this.setState((prev) => ({
            deleteModal: {
                ...prev.deleteModal,
                confirmation: event.target.value
            }
        }));
    }

    deleteRequest = async () => {
        const row = this.state.deleteModal.row;
        if (!row?.uuid || this.state.deleteModal.confirmation !== "DELETE") return;

        this.closeDeleteModal();
        this.setState({ actionLoading: `delete:${row.uuid}` });
        const response = await this.props.deleteEnvelopeGlobally({ uuid: row.uuid });
        this.setState({ actionLoading: "" });

        if (!response?.success) {
            this.props.sendAlert(responseError(response, "Signature request could not be deleted."), "error");
            return;
        }

        this.props.sendAlert("Signature request deleted.", "success");
        await this.loadData();
    }

    onPageChange = (page) => {
        this.setState({ page: Number(page) });
    }

    onColumnClick = (header) => {
        const index = this.headers.findIndex((item) => item.header === header.header && item.source === header.source);
        this.setState({
            index,
            direction: this.state.index === index && this.state.direction === "DESC" ? "ASC" : "DESC",
            order: header.source,
            page: 0
        });
    }

    onSearch = (search) => {
        this.setState({ search, page: 0 }, this.refreshPages);
    }

    onSize = (size) => {
        this.setState({ size: Number(size), page: 0 }, this.refreshPages);
    }

    get headers() {
        return [
            {
                header: "Request",
                sortable: true,
                source: "title",
                className: "text-muted text-small text-uppercase w-30",
                onRender: this.onRequestFormat
            },
            {
                header: "Status",
                sortable: true,
                source: "status",
                className: "text-muted text-small text-uppercase w-15",
                onRender: this.onStatusFormat
            },
            {
                header: "Signers",
                sortable: false,
                source: "uuid",
                className: "text-muted text-small text-uppercase w-25",
                onRender: this.onSignersFormat
            },
            {
                header: "Sent",
                sortable: true,
                source: "sentAt",
                className: "text-muted text-small text-uppercase w-15",
                onRender: this.onDateFormat
            },
            {
                header: "Completed",
                sortable: true,
                source: "completedAt",
                className: "text-muted text-small text-uppercase w-10",
                onRender: this.onDateFormat
            },
            {
                header: "",
                sortable: false,
                source: "uuid",
                className: "text-muted text-small text-uppercase w-5 text-end",
                onRender: this.onActionFormat
            }
        ];
    }

    render() {
        const row = this.state.menu.row || {};
        const remindingDisabled = !canRemind(row);
        const busy = Boolean(this.state.actionLoading);
        const hasItems = (this.state.items || []).length > 0;
        const deleteTitle = envelopeTitle(this.state.deleteModal.row || {});
        const deleteEnabled = this.state.deleteModal.confirmation === "DELETE" && !busy;

        if (!this.state.loading && !this.state.error && !hasItems) {
            return null;
        }

        return (
            <Card body className="mb-5">
                <Row id="signature-requests" className="g-3" style={{ scrollMarginTop: "90px" }}>
                    <Col xs="12" className="d-flex align-items-center justify-content-between">
                        <h2 className="small-title mb-0">Signature Requests</h2>
                        <div className="d-flex align-items-center gap-2">
                            {busy ? <Spinner animation="border" size="sm" /> : null}
                            {this.state.loading ? <Spinner animation="border" size="sm" /> : <Badge bg="light" text="dark">{this.state.items.length}</Badge>}
                        </div>
                    </Col>
                    {this.state.error ? (
                        <Col xs="12" className="text-danger">
                            {this.state.error}
                        </Col>
                    ) : null}
                    <Col xs="12">
                        <Table
                            headers={this.headers}
                            page={this.pagedItems}
                            sort={{ index: this.state.index, direction: this.state.direction }}
                            size={this.state.size}
                            index={this.state.page}
                            pages={this.state.pages}
                            onColumnClick={this.onColumnClick}
                            onPageChange={this.onPageChange}
                            onSearch={this.onSearch}
                            onSize={this.onSize}
                            searchable
                            sizable
                        />
                    </Col>
                </Row>
                <Overlay
                    target={this.state.menu.target}
                    show={this.state.menu.show}
                    placement="bottom-end"
                    rootClose
                    onHide={this.closeHamburger}
                >
                    {(props) => (
                        <Popover {...props}>
                            <Popover.Body>
                                <Row className="mb-2">
                                    <Col className="d-flex flex-row mt-1">
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 me-2 btn-sm small text-start"
                                            onClick={this.duplicateRequest}
                                            disabled={busy}
                                        >
                                            Duplicate
                                        </button>
                                    </Col>
                                </Row>
                                <Row className="mb-2">
                                    <Col className="d-flex flex-row mt-1">
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 me-2 btn-sm small text-start"
                                            onClick={this.remindRequest}
                                            disabled={busy || remindingDisabled}
                                            title={remindingDisabled ? "Only active requests can be reminded." : undefined}
                                        >
                                            Remind
                                        </button>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col className="d-flex flex-row mt-1">
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 me-2 btn-sm small text-start text-danger"
                                            onClick={this.openDeleteModal}
                                            disabled={busy}
                                        >
                                            Delete
                                        </button>
                                    </Col>
                                </Row>
                            </Popover.Body>
                        </Popover>
                    )}
                </Overlay>
                <ConfirmModal
                    show={this.state.deleteModal.show}
                    title="Delete Signature Request"
                    body="This deletes the signature request globally, including Sign records, Matters progress notes, and request-owned signature files."
                    onCancel={this.closeDeleteModal}
                    buttons={[
                        {
                            caption: "delete",
                            variant: "danger",
                            display: true,
                            enabled: deleteEnabled,
                            onEnabledClick: this.deleteRequest
                        },
                        {
                            default: true,
                            caption: "cancel",
                            variant: "primary",
                            display: true,
                            enabled: true,
                            onEnabledClick: this.closeDeleteModal
                        }
                    ]}
                >
                    <div className="mt-3">
                        <div className="fw-bold">{deleteTitle}</div>
                        <div className="text-muted mb-3">Type DELETE to confirm.</div>
                        <div className="top-label">
                            <Form.Control
                                value={this.state.deleteModal.confirmation}
                                onChange={this.onDeleteConfirmationChange}
                                autoFocus
                            />
                            <Form.Label>CONFIRMATION TEXT</Form.Label>
                        </div>
                    </div>
                </ConfirmModal>
            </Card>
        );
    }
}

SignatureRequests.propTypes = {
    person: PropTypes.string.isRequired,
    navigate: PropTypes.func.isRequired,
    deleteEnvelopeGlobally: PropTypes.func.isRequired,
    duplicateEnvelope: PropTypes.func.isRequired,
    listEnvelopes: PropTypes.func.isRequired,
    remindEnvelope: PropTypes.func.isRequired,
    sendAlert: PropTypes.func.isRequired
};

export default withNavigation(connect(null, {
    deleteEnvelopeGlobally,
    duplicateEnvelope,
    listEnvelopes,
    remindEnvelope,
    sendAlert
})(SignatureRequests));
